import type {Workbook} from 'exceljs'

/** `_rels/.rels` → "", `xl/worksheets/_rels/sheet1.xml.rels` → "xl/worksheets/". */
const relsBaseDir = (relsPath: string): string => relsPath.slice(0, relsPath.lastIndexOf('_rels/'))

/** Path of `target` (from the package root) relative to the directory `baseDir`. */
const relativePath = (baseDir: string, target: string): string => {
  const from = baseDir.split('/').filter(Boolean)
  const to = target.split('/').filter(Boolean)
  let shared = 0
  while (shared < from.length && shared < to.length - 1 && from[shared] === to[shared]) shared++
  return [...from.slice(shared).map(() => '..'), ...to.slice(shared)].join('/')
}

/**
 * The same .xlsx with every internal relationship target made relative, or null when it has no
 * absolute target (or is not a zip). Only `Target="/…"` of non-external relationships changes.
 */
export const relativiseXlsxRelationships = async (
  data: ArrayBuffer
): Promise<ArrayBuffer | null> => {
  const JSZip = (await import('jszip')).default
  let zip: InstanceType<typeof JSZip>
  try {
    zip = await JSZip.loadAsync(data)
  } catch {
    return null
  }
  let changed = false
  const relsFiles = Object.keys(zip.files).filter((path) => /(^|\/)_rels\/[^/]*\.rels$/.test(path))
  for (const path of relsFiles) {
    const xml = await zip.file(path)!.async('string')
    const baseDir = relsBaseDir(path)
    const next = xml.replace(/<Relationship\b[^>]*>/g, (element) => {
      if (/TargetMode\s*=\s*"External"/i.test(element)) return element
      return element.replace(/(\sTarget\s*=\s*")\/([^"]*)"/, (_m, head: string, target: string) => {
        changed = true
        return `${head}${relativePath(baseDir, target)}"`
      })
    })
    if (next !== xml) zip.file(path, next)
  }
  return changed ? zip.generateAsync({type: 'arraybuffer'}) : null
}

/**
 * An .xlsx loaded into an exceljs Workbook.
 *
 * exceljs 4.4 only follows RELATIVE relationship targets ("../tables/table1.xml"). An absolute
 * one ("/xl/tables/table1.xml") is valid OOXML — Excel opens it, Google Sheets and several
 * generators write it — but makes `xlsx.load` crash ("Cannot read properties of undefined
 * (reading 'name')" for a sheet with a table). Such a file is rewritten with relative targets
 * and loaded again; any other failure is rethrown as it was.
 */
export const loadExcelWorkbook = async (data: ArrayBuffer): Promise<Workbook> => {
  const {Workbook} = await import('exceljs')
  try {
    const workbook = new Workbook()
    await workbook.xlsx.load(data)
    return workbook
  } catch (error) {
    const repaired = await relativiseXlsxRelationships(data)
    if (!repaired) throw error
    const workbook = new Workbook()
    await workbook.xlsx.load(repaired)
    return workbook
  }
}
